import React, { useState } from 'react';
import axios from 'axios';
import { Crown, Check, X, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIPSection = () => {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', notes: '' });

  const handle = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error('Please fill in name, email and phone');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/vip-requests`, form);
      setSubmitted(true);
      toast.success('VIP request sent! We\'ll be in touch soon.');
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setOpen(false);
    setTimeout(() => {
      setSubmitted(false);
      setForm({ name: '', email: '', phone: '', notes: '' });
    }, 300);
  };

  const perks = [
    '15% off every detail, every time',
    'Priority booking slots',
    'Exclusive seasonal offers',
    'Free reminder service'
  ];

  return (
    <>
      <section id="vip" className="py-24 bg-gradient-to-br from-slate-950 via-slate-900 to-amber-950 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-amber-500 rounded-full blur-3xl opacity-10"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-400 rounded-full blur-3xl opacity-10"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div>
              <div className="inline-flex items-center px-4 py-1.5 bg-amber-400/10 border border-amber-400/30 rounded-full mb-6">
                <Crown className="w-4 h-4 text-amber-400 mr-2" />
                <span className="text-sm font-semibold text-amber-300">Exclusive Membership</span>
              </div>
              <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 leading-tight">
                Become a <span className="bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">ProClean VIP.</span>
              </h2>
              <p className="text-lg sm:text-xl text-slate-300 mb-8 leading-relaxed">
                For our loyal customers — request to join after your first detail and unlock <strong className="text-amber-300">15% off every booking</strong>, forever.
              </p>

              <ul className="space-y-3 mb-8">
                {perks.map((perk, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-amber-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-amber-300" strokeWidth={3} />
                    </div>
                    <span className="text-slate-200 font-medium">{perk}</span>
                  </li>
                ))}
              </ul>

              <Button
                data-testid="vip-request-btn"
                onClick={() => setOpen(true)}
                size="lg"
                className="bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-slate-900 rounded-xl px-8 py-6 text-base font-extrabold shadow-2xl shadow-amber-500/30 hover:shadow-amber-400/40 transition-all duration-300 hover:scale-[1.02] group"
              >
                <Crown className="w-5 h-5 mr-2" />
                Request VIP Status
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <p className="text-xs text-slate-400 mt-4">
                * Eligible after your first completed detail. Manual approval required.
              </p>
            </div>

            {/* Right: VIP Card */}
            <div className="relative">
              <div className="absolute -inset-1 bg-gradient-to-r from-amber-400 to-yellow-300 rounded-3xl blur-xl opacity-30"></div>
              <div className="relative bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-8 sm:p-10 border border-amber-400/20 shadow-2xl">
                <div className="flex items-center justify-between mb-8">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-amber-300 font-bold mb-1">VIP Member Card</div>
                    <div className="text-2xl font-extrabold text-white">ProClean</div>
                  </div>
                  <Crown className="w-12 h-12 text-amber-400" />
                </div>

                <div className="mb-8">
                  <div className="text-xs uppercase text-slate-400 font-bold tracking-wider mb-2">Your benefit</div>
                  <div className="text-5xl sm:text-6xl font-extrabold bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-400 bg-clip-text text-transparent">
                    15% OFF
                  </div>
                  <div className="text-slate-300 mt-2">Every detail, every time.</div>
                </div>

                <div className="pt-6 border-t border-white/10">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Status</div>
                      <div className="text-amber-300 font-bold mt-1">★ Premium</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-400 uppercase tracking-wider font-bold">Validity</div>
                      <div className="text-white font-bold mt-1">Lifetime</div>
                    </div>
                  </div>
                </div>

                {/* Sparkle decoration */}
                <Sparkles className="absolute top-6 right-20 w-5 h-5 text-amber-300/50" />
                <Sparkles className="absolute bottom-20 right-12 w-4 h-4 text-amber-300/40" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-[100] bg-slate-900/80 backdrop-blur-md flex items-center justify-center p-4 animate-fade-in"
          onClick={closeModal}
          data-testid="vip-modal"
        >
          <div
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-fade-in-up"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              data-testid="vip-modal-close"
              className="absolute top-4 right-4 w-9 h-9 bg-slate-100 hover:bg-slate-200 rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4 text-slate-600" />
            </button>

            {submitted ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-yellow-300 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Crown className="w-8 h-8 text-slate-900" />
                </div>
                <h3 className="text-2xl font-extrabold text-slate-900 mb-2">Request Sent!</h3>
                <p className="text-slate-600 mb-6">
                  Thanks {form.name.split(' ')[0]}! We'll review your request and contact you within 48 hours to confirm your VIP status.
                </p>
                <Button onClick={closeModal} className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl px-8">
                  Close
                </Button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-2">
                  <Crown className="w-6 h-6 text-amber-500" />
                  <h3 className="text-2xl font-extrabold text-slate-900">Request VIP Status</h3>
                </div>
                <p className="text-slate-600 text-sm mb-6">
                  Fill in your details — we'll verify your booking history and confirm your VIP membership.
                </p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="vip-name" className="text-sm font-semibold text-slate-700">Full Name *</Label>
                    <Input data-testid="vip-form-name" id="vip-name" value={form.name} onChange={(e) => handle('name', e.target.value)} required className="mt-1.5 rounded-xl h-11 border-slate-200" placeholder="John Smith" />
                  </div>
                  <div>
                    <Label htmlFor="vip-email" className="text-sm font-semibold text-slate-700">Email *</Label>
                    <Input data-testid="vip-form-email" id="vip-email" type="email" value={form.email} onChange={(e) => handle('email', e.target.value)} required className="mt-1.5 rounded-xl h-11 border-slate-200" placeholder="john@example.com" />
                  </div>
                  <div>
                    <Label htmlFor="vip-phone" className="text-sm font-semibold text-slate-700">Phone *</Label>
                    <Input data-testid="vip-form-phone" id="vip-phone" type="tel" value={form.phone} onChange={(e) => handle('phone', e.target.value)} required className="mt-1.5 rounded-xl h-11 border-slate-200" placeholder="0412 345 678" />
                  </div>
                  <div>
                    <Label htmlFor="vip-notes" className="text-sm font-semibold text-slate-700">Notes (Optional)</Label>
                    <Textarea data-testid="vip-form-notes" id="vip-notes" value={form.notes} onChange={(e) => handle('notes', e.target.value)} className="mt-1.5 rounded-xl border-slate-200 resize-none" rows={3} placeholder="Tell us about your last detail or anything else..." />
                  </div>
                  <Button data-testid="vip-form-submit" type="submit" disabled={submitting} className="w-full bg-gradient-to-r from-amber-400 to-yellow-300 hover:from-amber-300 hover:to-yellow-200 text-slate-900 rounded-xl py-6 font-extrabold">
                    {submitting ? 'Sending...' : (<>Submit VIP Request<ArrowRight className="w-4 h-4 ml-2" /></>)}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VIPSection;
